import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Award, Building2, Compass, ShieldCheck, Users } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { cn } from "@/lib/utils";
import { PageHead } from "@/components/PageHead";
import { usePageSeo } from "@/hooks/use-wp-seo";

type Value = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

type TimelineMilestone = {
  year: string;
  headline: string;
  detail: string;
};

type LeadershipMember = {
  name: string;
  role: string;
  bio: string;
};

const companyValues: Value[] = [
  {
    title: "Invent together",
    description: "We partner with customers, regulators, and payments innovators to design solutions nobody delivers alone.",
    icon: <Users className="h-5 w-5" />,
  },
  {
    title: "Engineer trust",
    description: "Resilience, transparency, and compliance are baked into every service, dashboard, and workflow we ship.",
    icon: <ShieldCheck className="h-5 w-5" />,
  },
  {
    title: "Move with purpose",
    description: "We measure impact through customer outcomes—launch velocity, cost efficiency, and new revenue unlocked.",
    icon: <Compass className="h-5 w-5" />,
  },
];

const milestones: TimelineMilestone[] = [
  {
    year: "2018",
    headline: "Onplexus founded",
    detail: "Treasury and product leaders join forces to simplify currency orchestration for global scale-ups.",
  },
  {
    year: "2020",
    headline: "MoEX platform launch",
    detail: "Our FX intelligence engine goes live, powering price and hedge automation across the first 10 regions.",
  },
  {
    year: "2022",
    headline: "Global network expansion",
    detail: "Partnerships with tier-one banks and alternative payment rails bring coverage to 180+ corridors.",
  },
  {
    year: "2024",
    headline: "Orchestration suite",
    detail: "Unified modules for pricing, routing, compliance, and analytics accelerate customers’ launch timelines by 74%.",
  },
];

const leadership: LeadershipMember[] = [
  {
    name: "Amelia Sato",
    role: "Chief Executive Officer",
    bio: "Former global head of treasury at a Fortune 100 fintech, Amelia drives Onplexus vision and customer success across continents.",
  },
  {
    name: "Luka Fernández",
    role: "Chief Product Officer",
    bio: "Previously built scaling payments systems at top SaaS platforms; now leads product strategy and roadmap execution.",
  },
  {
    name: "Priya Deshmukh",
    role: "Chief Technology Officer",
    bio: "Builder of mission-critical infrastructure for banks and cloud providers, Priya steers engineering, security, and innovation.",
  },
  {
    name: "Jonah Blakely",
    role: "Chief Revenue Officer",
    bio: "Enterprise GTM veteran focused on long-term partnerships and measurable customer outcomes.",
  },
];

function RevealSection({
  children,
  className,
  delay = 0,
  id,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  id?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <motion.section
      ref={ref}
      className={cn(className)}
      id={id}
      initial={{ opacity: 0, y: 56 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 56 }}
      transition={{ duration: 0.8, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    >
      {children}
    </motion.section>
  );
}

export default function Company() {
  const { data: pageSeo } = usePageSeo(["company"]);
  const valueCards = useMemo(
    () =>
      companyValues.map((value, index) => (
        <RevealSection
          key={value.title}
          delay={0.05 * index}
          className="flex h-full flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 px-6 py-6 shadow-[0_30px_90px_-50px_rgba(0,0,0,0.9)]"
        >
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-onplexus-gold/30 bg-onplexus-gold/10 text-onplexus-gold">
            {value.icon}
          </div>
          <h3 className="text-2xl font-semibold text-white">{value.title}</h3>
          <p className="text-sm text-white/70">{value.description}</p>
        </RevealSection>
      )),
    []
  );

  const headTitle = pageSeo?.title ?? "Company mission and leadership | OnPlexus";
  const headDescription = pageSeo?.description ?? undefined;
  const canonicalPath = "/company";

  return (
    <div className="min-h-screen bg-onplexus-dark text-white">
      <PageHead
        title={headTitle}
        description={headDescription}
        canonical={canonicalPath}
        seo={pageSeo}
      />
      <Header />
      <main className="pt-40 pb-24 px-4">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-16">
          <RevealSection className="overflow-hidden rounded-[40px] border border-white/10 bg-gradient-to-br from-onplexus-darker via-[#161a28] to-[#0d0f17] px-10 pb-14 pt-16 shadow-[0_60px_160px_-60px_rgba(0,0,0,0.95)]" id="mission">
            <div className="flex flex-col gap-12 md:flex-row md:items-center">
              <div className="flex-1 space-y-6">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-white/70">
                  Company
                </span>
                <h1 className="text-4xl font-semibold leading-tight text-white md:text-5xl">
                  Currency orchestration for the teams building tomorrow's financial infrastructure
                </h1>
                <p className="max-w-xl text-lg text-white/70">
                  Onplexus gives global enterprises the ability to launch, scale, and govern currency flows with confidence. Our mission is to connect every region, rail, and team so finance innovation never slows.
                </p>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Link
                    to="/careers"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-onplexus-gold px-6 py-3 text-base font-semibold text-onplexus-dark transition hover:bg-onplexus-gold-bright"
                  >
                    Explore careers
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/company/our-journey"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-transparent px-6 py-3 text-base font-semibold text-white transition hover:border-onplexus-gold/40 hover:text-onplexus-gold"
                  >
                    Read our story
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              </div>
              <motion.div
                className="relative flex flex-1 items-center justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.23, 1, 0.32, 1] }}
              >
                <div className="relative w-full max-w-[360px] overflow-hidden rounded-[32px] border border-onplexus-gold/30 bg-white/5 px-6 py-8">
                  <span className="text-sm font-semibold uppercase tracking-[0.3em] text-white/60">Impact snapshot</span>
                  <div className="mt-6 space-y-4 text-sm text-white/70">
                    <p className="rounded-2xl border border-white/10 bg-onplexus-darker/70 px-4 py-4">
                      Trusted by global banks, fintechs, and enterprises operating across 180+ markets.
                    </p>
                    <p className="rounded-2xl border border-white/10 bg-onplexus-darker/70 px-4 py-4">
                      72-hour launch playbooks help customers move faster without compromising governance.
                    </p>
                    <p className="rounded-2xl border border-white/10 bg-onplexus-darker/70 px-4 py-4">
                      4.7× average operational lift achieved across treasury and product teams.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </RevealSection>

          <RevealSection className="grid gap-6 md:grid-cols-3" id="values">
            {valueCards}
          </RevealSection>

          <RevealSection className="rounded-[36px] border border-white/10 bg-onplexus-darker/60 px-8 py-12" id="leadership">
            <header className="space-y-4 text-center">
              <h2 className="text-3xl font-semibold text-white md:text-4xl">Leadership team</h2>
              <p className="mx-auto max-w-2xl text-white/70">
                We bring together builders from payments, finance, and enterprise SaaS who understand the stakes of global operations.
              </p>
            </header>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {leadership.map((member, index) => (
                <RevealSection
                  key={member.name}
                  delay={0.05 * index}
                  className="flex h-full flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 px-6 py-6"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-white">{member.name}</h3>
                      <p className="text-sm text-onplexus-gold">{member.role}</p>
                    </div>
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-onplexus-gold/30 bg-onplexus-gold/10 text-onplexus-gold">
                      <Award className="h-5 w-5" />
                    </div>
                  </div>
                  <p className="text-sm text-white/70">{member.bio}</p>
                  <Link
                    to="/support-hub#contact"
                    className="mt-auto inline-flex w-fit items-center gap-2 rounded-full border border-onplexus-gold/40 px-4 py-2 text-sm font-semibold text-onplexus-gold transition hover:bg-onplexus-gold hover:text-onplexus-dark"
                  >
                    Connect
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </RevealSection>
              ))}
            </div>
          </RevealSection>

          <RevealSection className="rounded-[36px] border border-white/10 bg-onplexus-darker/70 px-8 py-12" id="timeline">
            <header className="space-y-4 text-center">
              <h2 className="text-3xl font-semibold text-white md:text-4xl">Our journey</h2>
              <p className="mx-auto max-w-2xl text-white/70">
                From a small team tackling cross-border finance headaches to a global platform delivering full currency orchestration.
              </p>
            </header>
            <div className="mt-10 grid gap-6 md:grid-cols-4">
              {milestones.map((milestone, index) => (
                <RevealSection
                  key={milestone.year}
                  delay={0.04 * index}
                  className="flex h-full flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 px-6 py-6 text-left"
                >
                  <div className="inline-flex items-center gap-2 text-onplexus-gold">
                    <Building2 className="h-5 w-5" />
                    <span className="text-xs font-semibold uppercase tracking-[0.3em]">{milestone.year}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">{milestone.headline}</h3>
                  <p className="text-sm text-white/70">{milestone.detail}</p>
                </RevealSection>
              ))}
            </div>
          </RevealSection>

          <RevealSection className="rounded-[36px] border border-white/10 bg-onplexus-darker/60 px-8 py-12" id="culture">
            <div className="grid gap-10 md:grid-cols-[1.1fr,0.9fr] md:items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-semibold text-white md:text-4xl">Where we operate</h2>
                <p className="text-white/70">
                  Onplexus teams are located in London, Singapore, Toronto, São Paulo, and remote hubs across 18 countries. We believe diversity of expertise and culture creates better products.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-6">
                    <p className="text-xs uppercase tracking-[0.3em] text-white/50">Team</p>
                    <p className="mt-2 text-3xl font-semibold text-onplexus-gold">180+</p>
                    <p className="mt-2 text-sm text-white/70">Experts across engineering, finance, compliance, design, and delivery.</p>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-6">
                    <p className="text-xs uppercase tracking-[0.3em] text-white/50">Presence</p>
                    <p className="mt-2 text-3xl font-semibold text-onplexus-gold">4</p>
                    <p className="mt-2 text-sm text-white/70">Regional hubs enabling follow-the-sun coverage.</p>
                  </div>
                </div>
              </div>
              <motion.div
                className="relative flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.23, 1, 0.32, 1] }}
              >
                <div className="relative w-full max-w-[320px] overflow-hidden rounded-[32px] border border-onplexus-gold/30 bg-white/5 px-6 py-8 text-sm text-white/70">
                  <span className="text-sm font-semibold uppercase tracking-[0.3em] text-white/60">Inside Onplexus</span>
                  <ul className="mt-6 space-y-4">
                    <li className="rounded-2xl border border-white/10 bg-onplexus-darker/70 px-4 py-4">
                      Hybrid-first culture with quarterly global summit weeks.
                    </li>
                    <li className="rounded-2xl border border-white/10 bg-onplexus-darker/70 px-4 py-4">
                      Volunteer days and pro-bono orchestration for NGOs fighting financial crime.
                    </li>
                    <li className="rounded-2xl border border-white/10 bg-onplexus-darker/70 px-4 py-4">
                      Learning stipends and guilds for AI, compliance, and product experimentation.
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </RevealSection>

          <RevealSection className="overflow-hidden rounded-[36px] border border-white/10 bg-onplexus-darker/70 px-8 py-12 text-center shadow-[0_30px_120px_-70px_rgba(0,0,0,0.95)]" id="careers">
              <div className="space-y-6">
                <h2 className="text-3xl font-semibold text-white md:text-4xl">Join us</h2>
                <p className="mx-auto max-w-2xl text-white/70">
                  We’re hiring mission-driven builders across engineering, product, compliance, design, and customer success. Ship with a team that cares about outcomes and craft.
                </p>
                <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                  <Link
                    to="/careers#roles"
                    className="inline-flex items-center gap-2 rounded-2xl bg-onplexus-gold px-6 py-3 text-base font-semibold text-onplexus-dark transition hover:bg-onplexus-gold-bright"
                  >
                    View open roles
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/careers#refer"
                    className="inline-flex items-center gap-2 rounded-2xl border border-white/20 px-6 py-3 text-base font-semibold text-white transition hover:border-onplexus-gold/40 hover:text-onplexus-gold"
                  >
                    Refer a candidate
                  </Link>
                </div>
              </div>
          </RevealSection>
        </div>
      </main>
      <Footer />
    </div>
  );
}
